import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, CheckCircle, Clock, Sparkle, ChartLine, ClipboardText, Warning, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";
import EmbeddedCheckoutDialog from "@/components/WhiteLabelCheckoutDialog";
import InvoicesDialog from "@/components/InvoicesDialog";
import CancelSubscriptionDialog from "@/components/CancelSubscriptionDialog";
import ChangePlanDialog from "@/components/ChangePlanDialog";
import ReactivateSubscriptionDialog from "@/components/ReactivateSubscriptionDialog";
import UpdatePaymentMethodDialog from "@/components/UpdatePaymentMethodDialog";
import ChoosePlanDialog from "@/components/ChoosePlanDialog";

const STATUS = {
  trialing: { label: "Essai gratuit", tone: "bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6]" },
  trialing_expired: { label: "Essai terminé", tone: "bg-[#FBE8E1] text-[#B9502C] border border-[#F0B686]" },
  active: { label: "Abonnement actif", tone: "bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6]" },
  past_due: { label: "Paiement en retard", tone: "bg-[#FFE0DC] text-[#8B2A1E] border border-[#EEC1BB]" },
  canceled: { label: "Annulé", tone: "bg-[#F4F7FB] text-[#5F758F]" },
  unpaid: { label: "Impayé", tone: "bg-[#FFE0DC] text-[#8B2A1E] border border-[#EEC1BB]" },
};

// Absolute Paris-timezone date for a past trial-end. Practitioners preferred
// a stable "Terminé le JJ/MM/AAAA" over relative "il y a N jours".
const formatTrialEnd = (iso) => {
  if (!iso) return "";
  const end = new Date(iso);
  return `le ${new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    day: "2-digit", month: "2-digit", year: "numeric",
  }).format(end)}`;
};

const BASE_FEATURES = [
  "Questionnaires illimités",
  "Bibliothèque validée (EVA, PHQ-9, GAD-7…)",
  "Création de questionnaire avec IA",
  "Envois illimités",
  "Accès collaborateur et secrétariat",
  "Conformité RGPD & archivage sur serveur HDS en France",
];

const PREMIUM_FEATURES = [
  "Tout ce qui est inclus dans Standard, plus :",
  "Statistiques anonymisées",
  "Comparaison de cohortes",
  "Analyse des données de vos compte-rendus par IA",
  "Accès assistant de recherche clinique",
  "Exports CSV",
];

function PlanCard({ name, subtitle, price, priceSuffix, badge, savings, current, highlighted, onSelect, features, icon: Icon, iconClass, disabled, ctaLabel, lockedReason }) {
  const isLocked = !!lockedReason && !current;
  return (
    <div className={`border rounded-2xl p-6 flex flex-col transition-all ${highlighted ? "border-[#3A8FA5] bg-white ring-2 ring-[#3A8FA5]/20 shadow-[0_2px_8px_rgba(58,143,165,0.08)]" : "border-[#E1E9F0] bg-white"} ${isLocked ? "opacity-75" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={22} weight="duotone" className={iconClass || "text-[#3A8FA5]"} />
          <h3 className="text-xl font-bold tracking-tight text-[#1E3A5F] text-[#1E3A5F]">{name}</h3>
        </div>
        {current && <Badge className="bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6] hover:bg-[#E6F1F5]">Abonnement actuel</Badge>}
        {!current && badge && <Badge className="bg-[#3A8FA5] text-white hover:bg-[#3A8FA5]">{badge}</Badge>}
      </div>
      {subtitle && <p className="text-sm italic mb-3 text-[#5F758F]">{subtitle}</p>}
      <div className="flex items-baseline gap-2 mb-1">
        <p className="text-4xl font-bold tracking-tight text-[#1E3A5F] tabular-nums">{price}€</p>
        <p className="text-sm text-[#5F758F]">{priceSuffix || "/ mois TTC"}</p>
      </div>
      {savings && <p className="text-xs text-[#3A8FA5] font-medium mb-3">{savings}</p>}
      {!savings && <div className="mb-3" />}
      <ul className="space-y-2 text-sm text-[#1E3A5F] mb-5 flex-1">
        {features.map((f) => {
          const isHeading = f.trim().endsWith(":");
          if (isHeading) {
            return (
              <li key={f} className="font-semibold text-[#1E3A5F] pt-1">{f}</li>
            );
          }
          return (
            <li key={f} className="flex items-start gap-2">
              <CheckCircle size={16} weight="fill" className="text-[#3A8FA5] shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          );
        })}
      </ul>
      {isLocked && (
        <div className="mb-3 flex items-start gap-2 text-xs text-[#8A3E20] bg-[#FBE8E1] border border-[#E27C4F]/40 rounded-md p-2.5" data-testid={`plan-locked-${name.toLowerCase()}`}>
          <Lock size={14} weight="duotone" className="shrink-0 mt-0.5 text-[#B9502C]" />
          <span>{lockedReason}</span>
        </div>
      )}
      <Button
        onClick={onSelect}
        disabled={current || disabled || isLocked}
        title={isLocked ? lockedReason : undefined}
        className="rounded-[10px] w-full font-semibold bg-[#3A8FA5] hover:bg-[#164E5F] text-white disabled:opacity-40"
        data-testid={`billing-select-${name.toLowerCase()}`}
      >
        {isLocked ? <Lock size={16} className="mr-2" /> : <CreditCard size={16} className="mr-2" />}
        {current ? "Abonnement actuel" : (isLocked ? "Verrouillé" : (ctaLabel || "Souscrire"))}
      </Button>
    </div>
  );
}

export default function BillingPanel() {
  const { refresh: refreshAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState("premium");
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [changePlan, setChangePlan] = useState(null); // target plan key, null = closed
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [updatePmOpen, setUpdatePmOpen] = useState(false);
  const [updatePmContext, setUpdatePmContext] = useState("regularize");
  const [choosePlanOpen, setChoosePlanOpen] = useState(false);
  const [yearly, setYearly] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
      // Sync the AuthProvider so the top AppShell banner (which reads
      // cancel_at_period_end / scheduled_end from `useAuth().user`) picks
      // up cancel / reactivate / plan-change updates without a full reload.
      refreshAuth?.();
    } catch (err) { log.error("billing status", err); }
  };
  useEffect(() => { load(); }, []);

  const startCheckout = (plan) => {
    setCheckoutPlan(plan);
    setCheckoutOpen(true);
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      log.error("portal", err);
      toast.error(err?.response?.data?.detail || "Impossible d'ouvrir le portail");
      setBusy(false);
    }
  };

  if (!status) return null;
  const isTrial = status.subscription_status === "trialing";
  const isActive = status.subscription_status === "active";
  const isPastDue = ["past_due", "unpaid"].includes(status.subscription_status);
  const canManageActive = isActive || isPastDue;
  const cancelScheduled = !!status.cancel_at_period_end || !!status.cancel_at;
  const scheduledEndDate = status.scheduled_end
    ? new Date(status.scheduled_end * 1000).toLocaleDateString("fr-FR")
    : null;
  const currentPlan = status.plan || "base";
  const PLAN_DETAILS = {
    base: { name: "Standard", commitment: "sans engagement", monthly: "29€" },
    base_yearly: { name: "Standard", commitment: "engagement annuel", monthly: "26,10€" },
    premium: { name: "Premium", commitment: "sans engagement", monthly: "49€" },
    premium_yearly: { name: "Premium", commitment: "engagement annuel", monthly: "44,10€" },
  };
  const planDetail = PLAN_DETAILS[currentPlan];
  const withinCommitment = !!status.within_commitment;
  const commitmentEndDate = status.commitment_end
    ? new Date(status.commitment_end * 1000).toLocaleDateString("fr-FR")
    : null;

  // Enforce the annual-commitment rules on the frontend (backend is source of
  // truth). During an active yearly commitment:
  //   - base_yearly can ONLY upgrade to premium_yearly
  //   - premium_yearly is fully locked until commitmentEnd
  const commitmentLock = (target) => {
    if (!withinCommitment) return "";
    if (currentPlan === "base_yearly") {
      if (target === "premium_yearly") return "";
      return `Vous êtes engagé jusqu'au ${commitmentEndDate} avec la formule Standard annuelle. Aucun changement de formule n'est possible avant cette date.`;
    }
    if (currentPlan === "premium_yearly") {
      return `Vous êtes engagé jusqu'au ${commitmentEndDate} avec la formule Premium annuelle. Aucun changement de formule n'est possible avant cette date.`;
    }
    return "";
  };

  const hasStripe = isActive || ["past_due", "canceled", "unpaid"].includes(status.subscription_status);
  const isTrialExpired = isTrial && !(status.trial_days_left > 0);
  const cfgKey = isTrialExpired ? "trialing_expired" : status.subscription_status;
  const cfg = cancelScheduled
    ? { label: "Résiliation prévue", tone: "bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6]" }
    : STATUS[cfgKey] || { label: status.subscription_status, tone: "bg-[#F4F7FB] text-[#5F758F]" };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-[#1E3A5F]">Mon abonnement</h2>
            <p className="text-sm text-[#5F758F] mt-1" data-testid="billing-plan-summary">
              {cancelScheduled && planDetail && scheduledEndDate
                ? `Résiliation prévue le ${scheduledEndDate} — accès Formule ${planDetail.name} maintenu jusqu'à cette date`
                : planDetail && (isActive || isPastDue)
                ? `Formule ${planDetail.name} — ${planDetail.commitment} — ${planDetail.monthly} / mois TTC`
                : status.subscription_status === "canceled"
                ? "Abonnement résilié — souscrivez à nouveau pour retrouver l'accès"
                : "Choisissez la formule adaptée à votre pratique"}
            </p>
          </div>
          {/* Badge redondant avec le grand encart rouge en dessous quand l'essai
              vient d'expirer — masqué dans ce cas pour éviter la double info. */}
          {!isTrialExpired && <Badge className={cfg.tone + " hover:" + cfg.tone.split(" ")[0]}>{cfg.label}</Badge>}
        </div>

        {isTrial && (
          <div
            className={
              isTrialExpired
                ? "bg-[#FBE8E1] border-2 border-[#E27C4F] rounded-[10px] p-4 mb-4 flex items-start gap-3"
                : "bg-[#F4F7FB] border border-[#E1E9F0] rounded-[10px] p-4 mb-4 flex items-start gap-3"
            }
            data-testid="trial-status-card"
          >
            <Clock
              size={20}
              weight="duotone"
              className={`shrink-0 mt-0.5 ${isTrialExpired ? "text-[#B9502C]" : "text-[#3A8FA5]"}`}
            />
            <div>
              <p className={`text-sm font-semibold ${isTrialExpired ? "text-[#8A3E20]" : "text-[#1E3A5F]"}`}>
                {isTrialExpired
                  ? `Votre période d'essai a pris fin ${formatTrialEnd(status.trial_ends_at)}`
                  : `${status.trial_days_left} jour${status.trial_days_left > 1 ? "s" : ""} d'essai restant${status.trial_days_left > 1 ? "s" : ""}`}
              </p>
              <p className={`text-xs mt-1 ${isTrialExpired ? "text-[#8A3E20]" : "text-[#5F758F]"}`}>
                {isTrialExpired
                  ? "Souscrivez un abonnement pour continuer à utiliser Questiomed."
                  : `${status.trial_ends_at ? `Fin d'essai le ${new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(status.trial_ends_at))}` : ""} — Toutes les fonctionnalités de l'offre Premium sont accessibles pendant l'essai, sans CB.`}
              </p>
            </div>
          </div>
        )}

        {isPastDue && !cancelScheduled && (
          <div
            className="bg-[#FBE8E1] border-2 border-[#E27C4F] rounded-[10px] p-4 mb-4 flex items-start gap-3"
            data-testid="past-due-card"
          >
            <Warning size={20} weight="duotone" className="text-[#B9502C] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#8A3E20]">
                Le prélèvement de votre abonnement a échoué
              </p>
              <p className="text-xs text-[#8A3E20] mt-1 leading-relaxed">
                {status.next_payment_attempt
                  ? `Nouvelle tentative automatique prévue le ${new Date(status.next_payment_attempt * 1000).toLocaleDateString("fr-FR")}. `
                  : "Une nouvelle tentative sera effectuée automatiquement. "}
                {status.suspension_at
                  ? `Sans régularisation, votre abonnement sera suspendu le ${new Date(status.suspension_at * 1000).toLocaleDateString("fr-FR")}. `
                  : ""}
                Mettez à jour votre moyen de paiement pour réactiver immédiatement votre accès — aucune donnée n'est perdue.
              </p>
              <Button
                onClick={() => { setUpdatePmContext("regularize"); setUpdatePmOpen(true); }}
                className="mt-3 rounded-[10px] bg-[#B9502C] hover:bg-[#8A3E20] text-white font-semibold"
                data-testid="update-payment-method-btn"
              >
                <CreditCard size={16} className="mr-2" />
                Mettre à jour mon moyen de paiement
              </Button>
            </div>
          </div>
        )}

        {cancelScheduled && scheduledEndDate && (
          <div
            className="bg-[#F4F7FB] border-2 border-[#3A8FA5]/40 rounded-[10px] p-4 mb-4 flex items-start gap-3"
            data-testid="cancel-scheduled-card"
          >
            <Warning size={20} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#1E3A5F]">
                Résiliation prévue le {scheduledEndDate}
              </p>
              <p className="text-xs text-[#3A5A70] mt-1 leading-relaxed">
                Vous conservez l'intégralité de vos fonctionnalités jusqu'à cette date. Vous pouvez annuler la résiliation ou souscrire à nouveau à tout moment avant celle-ci.
              </p>
            </div>
          </div>
        )}

        {isActive && withinCommitment && commitmentEndDate && !cancelScheduled && (
          <div
            className="bg-[#E6F1F5] border border-[#C7DEE6] rounded-[10px] p-4 mb-4 flex items-start gap-3"
            data-testid="commitment-info-card"
          >
            <Lock size={20} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-[#1E3A5F] font-semibold">
                Engagement annuel en cours jusqu'au {commitmentEndDate}
              </p>
              <p className="text-xs text-[#3A5A70] mt-1 leading-relaxed">
                {currentPlan === "base_yearly"
                  ? "Vous pouvez uniquement évoluer vers l'engagement annuel Premium. Le passage au mensuel sera possible à la fin de l'engagement."
                  : "Aucun changement de formule n'est possible avant la fin de l'engagement."}
              </p>
            </div>
          </div>
        )}

        {isActive && status.current_period_end && (
          <div className="bg-[#E6F1F5] border border-[#C7DEE6] rounded-[10px] p-4 mb-4 flex items-start gap-3">
            <CheckCircle size={20} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-[#1E3A5F] font-semibold">Prochain prélèvement le {new Date(status.current_period_end).toLocaleDateString("fr-FR")}</p>
            </div>
          </div>
        )}

        {canManageActive && (
          <div className="bg-white border border-[#E1E9F0] rounded-[10px] p-4 mb-4 flex items-center gap-3" data-testid="billing-payment-method-card">
            <CreditCard size={20} weight="duotone" className="text-[#3A8FA5] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.14em] uppercase text-[#5F758F] font-bold">Moyen de paiement</p>
              <p className="text-sm text-[#1E3A5F] font-semibold mt-0.5 truncate">
                {status.default_payment_method?.last4
                  ? <>Carte <span className="uppercase">{status.default_payment_method.brand}</span> •••• {status.default_payment_method.last4}
                      {status.default_payment_method.exp_month && status.default_payment_method.exp_year && (
                        <span className="text-[#5F758F] font-normal ml-2 text-xs">expire {String(status.default_payment_method.exp_month).padStart(2, "0")}/{String(status.default_payment_method.exp_year).slice(-2)}</span>
                      )}
                    </>
                  : "Aucune carte enregistrée"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setUpdatePmContext("change"); setUpdatePmOpen(true); }}
              className="text-xs font-semibold text-[#3A8FA5] hover:text-[#164E5F] underline underline-offset-2 whitespace-nowrap transition-colors shrink-0"
              data-testid="billing-change-card-btn"
            >
              {status.default_payment_method?.last4 ? "Modifier" : "Ajouter une carte"}
            </button>
          </div>
        )}

        {hasStripe && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => setInvoicesOpen(true)}
              variant="outline"
              className="rounded-[10px] font-semibold border-[#E1E9F0] hover:border-[#3A8FA5] hover:text-[#3A8FA5] transition-colors"
              data-testid="billing-invoices-btn"
            >
              Factures
            </Button>
            {canManageActive && !cancelScheduled && (() => {
              // Only show "Changer d'abonnement" when at least one other plan
              // is reachable — a premium_yearly practitioner locked by the
              // annual commitment has no options and would only see a modal
              // with everything greyed out.
              const anyReachable = ["base", "base_yearly", "premium", "premium_yearly"]
                .some((p) => p !== currentPlan && !commitmentLock(p));
              if (!anyReachable) return null;
              return (
                <Button
                  onClick={() => setChoosePlanOpen(true)}
                  className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold transition-colors"
                  data-testid="billing-change-plan-btn"
                >
                  Changer d'abonnement
                </Button>
              );
            })()}
            {canManageActive && !cancelScheduled && (
              <Button
                onClick={() => setCancelOpen(true)}
                className="rounded-[10px] bg-[#B9502C] hover:bg-[#8A3E20] text-white font-semibold transition-colors"
                data-testid="billing-cancel-btn"
              >
                Résilier
              </Button>
            )}
            {canManageActive && cancelScheduled && (
              <Button
                onClick={() => setReactivateOpen(true)}
                variant="outline"
                className="rounded-[10px] font-semibold border-[#E1E9F0] hover:border-[#3A8FA5] hover:text-[#3A8FA5] transition-colors"
                data-testid="billing-reactivate-btn"
              >
                Annuler la résiliation
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="pt-4">
        <h3 className="font-serif text-[22px] md:text-[24px] leading-tight tracking-tight text-[#1E3A5F] mb-5">
          Nos offres
        </h3>
        <div className="flex items-center justify-center -mb-2">
          <div className="inline-flex bg-white border border-[#E1E9F0] rounded-full p-1">
            <button
              onClick={() => setYearly(false)}
              className={`px-5 py-1.5 text-sm font-bold rounded-full transition-colors ${!yearly ? "bg-[#3A8FA5] text-white" : "text-[#5F758F] hover:text-[#1E3A5F]"}`}
              data-testid="billing-toggle-monthly"
            >Mensuel</button>
            <button
              onClick={() => setYearly(true)}
              className={`px-5 py-1.5 text-sm font-bold rounded-full transition-colors ${yearly ? "bg-[#3A8FA5] text-white" : "text-[#5F758F] hover:text-[#1E3A5F]"}`}
              data-testid="billing-toggle-yearly"
            >
              Annuel <span className="ml-1.5 font-bold">-10%</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PlanCard
          name="Standard"
          subtitle="Pour la pratique quotidienne"
          icon={ClipboardText}
          iconClass="text-[#3A8FA5]"
          price={yearly ? "26,10" : "29"}
          priceSuffix={yearly ? "/ mois TTC, soit 313,20€ / an, engagement annuel" : "/ mois TTC, sans engagement"}
          savings={yearly ? "Économisez 34,80€ / an" : null}
          badge={yearly ? "Économie de 10%" : null}
          features={BASE_FEATURES}
          current={isActive && (yearly ? currentPlan === "base_yearly" : currentPlan === "base")}
          onSelect={() => {
            const target = yearly ? "base_yearly" : "base";
            if (isActive) setChangePlan(target);
            else startCheckout(target);
          }}
          disabled={busy}
          lockedReason={isActive ? commitmentLock(yearly ? "base_yearly" : "base") : ""}
          ctaLabel={
            isActive
              ? (yearly
                  ? "Passer en Standard, avec engagement annuel"
                  : "Passer en Standard, sans engagement")
              : (isTrial ? "Choisir Standard" : "Souscrire")
          }
        />
        <PlanCard
          name="Premium"
          subtitle="Pour la recherche clinique"
          icon={ChartLine}
          price={yearly ? "44,10" : "49"}
          priceSuffix={yearly ? "/ mois TTC, soit 529,20€ / an, engagement annuel" : "/ mois TTC, sans engagement"}
          savings={yearly ? "Économisez 58,80€ / an" : null}
          badge={yearly ? "Économie de 10%" : null}
          features={PREMIUM_FEATURES}
          current={isActive && (yearly ? currentPlan === "premium_yearly" : currentPlan === "premium")}
          highlighted
          onSelect={() => {
            const target = yearly ? "premium_yearly" : "premium";
            if (isActive) setChangePlan(target);
            else startCheckout(target);
          }}
          disabled={busy}
          lockedReason={isActive ? commitmentLock(yearly ? "premium_yearly" : "premium") : ""}
          ctaLabel={
            isActive
              ? (yearly
                  ? "Passer en Premium, avec engagement annuel"
                  : "Passer en Premium, sans engagement")
              : (isTrial ? "Choisir Premium" : "Souscrire")
          }
        />
      </div>

      <div className="bg-[#E6F1F5] border-2 border-[#3A8FA5]/40 rounded-2xl p-5 shadow-[0_2px_8px_rgba(58,143,165,0.08)]">
        <div>
          <p className="text-sm text-[#1E3A5F] font-bold mb-1">
            {yearly ? "Engagement 12 mois — mensualités réduites de 10%" : "Sans engagement — résiliation en 1 clic"}
          </p>
          <p className="text-xs text-[#3A5A70] leading-relaxed">
            {yearly
              ? "Vous êtes prélevé chaque mois au tarif réduit pendant 12 mois. Résiliation possible à la fin de l'engagement."
              : "Vous pouvez changer de formule ou résilier à tout moment depuis la gestion d'abonnement."}
            {" "}Paiement 100% sécurisé — les données de carte ne transitent jamais par Questiomed.
          </p>
        </div>
      </div>
      <EmbeddedCheckoutDialog
        open={checkoutOpen}
        plan={checkoutPlan}
        onOpenChange={setCheckoutOpen}
        onCompleted={load}
      />
      <InvoicesDialog
        open={invoicesOpen}
        onOpenChange={setInvoicesOpen}
      />
      <CancelSubscriptionDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onCancelled={load}
      />
      <ChangePlanDialog
        open={!!changePlan}
        onOpenChange={(v) => { if (!v) setChangePlan(null); }}
        currentPlan={currentPlan}
        targetPlan={changePlan}
        defaultPaymentMethod={status.default_payment_method}
        onCardUpdated={load}
        onChanged={load}
      />
      <ReactivateSubscriptionDialog
        open={reactivateOpen}
        onOpenChange={setReactivateOpen}
        currentPlan={currentPlan}
        scheduledEndDate={scheduledEndDate}
        defaultPaymentMethod={status.default_payment_method}
        onCardUpdated={load}
        onReactivated={load}
      />
      <UpdatePaymentMethodDialog
        open={updatePmOpen}
        onOpenChange={setUpdatePmOpen}
        onCompleted={load}
        context={updatePmContext}
      />
      <ChoosePlanDialog
        open={choosePlanOpen}
        onOpenChange={setChoosePlanOpen}
        currentPlan={currentPlan}
        commitmentLock={commitmentLock}
        onSelect={(target) => setChangePlan(target)}
      />
    </div>
  );
}
